import {
  CampaignDetailStatsApiResponse,
  CampaignStatsEpisodeTop10Row,
  CampaignStatsShowTop10Row,
  CampaignStatsTop10TitleRow,
} from 'src/types/campaign';

export type AlignmentTop10Row = {
  name: string;
  impressions: number;
  violationReason?: string;
  /** Podcast / show title for episode alignment rows */
  showTitle?: string;
};

export type CampaignReportData = {
  reportingDate: string;
  /** From `overall_stats.total_impressions` */
  totalImpressions: number;
  showMatched: {
    uniqueShows: number;
    uniqueShowsPercent: number;
    impressions: number;
    impressionsPercent: number;
  };
  showUnmatched: {
    uniqueShows: number;
    uniqueShowsPercent: number;
    impressions: number;
    impressionsPercent: number;
  };
  episodeMatched: {
    uniqueEpisodes: number;
    uniqueEpisodesPercent: number;
    impressions: number;
    impressionsPercent: number;
  };
  episodeUnmatched: {
    uniqueEpisodes: number;
    uniqueEpisodesPercent: number;
    impressions: number;
    impressionsPercent: number;
  };
  episodeScored: {
    uniqueEpisodes: number;
    uniqueEpisodesPercent: number;
    impressions: number;
    impressionsPercent: number;
  };
  episodeUnscored: {
    uniqueEpisodes: number;
    uniqueEpisodesPercent: number;
    impressions: number;
    impressionsPercent: number;
  };
  top10ShowsByImpression: { name: string; impressions: number }[];
  top10EpisodesByImpression: { name: string; impressions: number }[];
  top5IabCategoriesByImpression: string[];
  top5GenresByImpression: string[];
  episodeAlignment: {
    impressionsAligned: number;
    impressionsAlignedPercent: number;
    episodesAligned: number;
    episodesAlignedPercent: number;
    impressionsMisaligned: number;
    impressionsMisalignedPercent: number;
    episodesMisaligned: number;
    episodesMisalignedPercent: number;
    top10MisalignedEpisodes: AlignmentTop10Row[];
    top10AlignedEpisodes: AlignmentTop10Row[];
  };
  showAlignment: {
    impressionsAligned: number;
    impressionsAlignedPercent: number;
    showsAligned: number;
    showsAlignedPercent: number;
    impressionsMisaligned: number;
    impressionsMisalignedPercent: number;
    showsMisaligned: number;
    showsMisalignedPercent: number;
    top10MisalignedShows: AlignmentTop10Row[];
    top10AlignedShows: AlignmentTop10Row[];
  };
};

const parseNum = (s: string | undefined): number =>
  s ? parseInt(String(s).replace(/,/g, ''), 10) || 0 : 0;
const parsePct = (s: string | undefined): number =>
  s ? parseFloat(String(s).replace(/%/g, '')) || 0 : 0;

const mapTop10TitleRows = (
  rows: CampaignStatsTop10TitleRow[] | undefined
): { name: string; impressions: number }[] =>
  (rows ?? []).map((row) => ({
    name: row.title,
    impressions: row.impressions,
  }));

const mapEpisodeTop10Rows = (
  rows: CampaignStatsEpisodeTop10Row[] | undefined
): AlignmentTop10Row[] =>
  (rows ?? []).map((row) => ({
    name: row.episode_title,
    showTitle: row.show_title,
    impressions: row.impressions,
    violationReason: row.violation_reason,
  }));

const mapShowTop10Rows = (
  rows: CampaignStatsShowTop10Row[] | undefined
): AlignmentTop10Row[] =>
  (rows ?? []).map((row) => ({
    name: row.show_title ?? '',
    impressions: row.impressions,
    violationReason: row.violation_reason,
  }));

export const getCampaignReportFromStats = (
  stats: CampaignDetailStatsApiResponse | null
): CampaignReportData | null => {
  if (!stats || stats.status !== 'success') return null;

  const sm = stats.shows_matching_stats;
  const em = stats.episodes_matching_stats;
  const se = stats.scored_episodes_stats;
  const overall = stats.overall_stats;

  const showMatch = {
    uniqueShows: parseNum(sm?.matched_unique_shows),
    uniqueShowsPercent: parsePct(sm?.matched_unique_shows_pct),
    impressions: parseNum(sm?.matched_unique_show_impressions),
    impressionsPercent: parsePct(sm?.matched_unique_show_impressions_pct),
  };
  const showUnmatch = {
    uniqueShows: parseNum(sm?.unmatched_unique_shows),
    uniqueShowsPercent: parsePct(sm?.unmatched_unique_shows_pct),
    impressions: parseNum(sm?.unmatched_unique_show_impressions),
    impressionsPercent: parsePct(sm?.unmatched_unique_show_impressions_pct),
  };
  const episodeMatch = {
    uniqueEpisodes: parseNum(em?.matched_unique_episodes),
    uniqueEpisodesPercent: parsePct(em?.matched_unique_episodes_pct),
    impressions: parseNum(em?.matched_unique_episode_impressions),
    impressionsPercent: parsePct(em?.matched_unique_episode_impressions_pct),
  };
  const episodeUnmatch = {
    uniqueEpisodes: parseNum(em?.unmatched_unique_episodes),
    uniqueEpisodesPercent: parsePct(em?.unmatched_unique_episodes_pct),
    impressions: parseNum(em?.unmatched_unique_episode_impressions),
    impressionsPercent: parsePct(em?.unmatched_unique_episode_impressions_pct),
  };
  const episodeScored = {
    uniqueEpisodes: parseNum(se?.scored_unique_episodes),
    uniqueEpisodesPercent: parsePct(se?.scored_unique_episodes_pct),
    impressions: parseNum(se?.scored_unique_episode_impressions),
    impressionsPercent: parsePct(se?.scored_unique_episode_impressions_pct),
  };
  const episodeUnscored = {
    uniqueEpisodes: parseNum(se?.unscored_unique_episodes),
    uniqueEpisodesPercent: parsePct(se?.unscored_unique_episodes_pct),
    impressions: parseNum(se?.unscored_unique_episode_impressions),
    impressionsPercent: parsePct(se?.unscored_unique_episode_impressions_pct),
  };

  const top10Shows = mapTop10TitleRows(stats.top10_shows_by_impression);
  const top10Eps = mapTop10TitleRows(stats.top10_eps_by_impression);
  const top5Iab = stats.top5_iab_categories_by_impression;
  const top5Genres = stats.top5_genres_by_impression;

  const showAligned = stats.show_aligned_stats;
  const episodeAligned = stats.episode_aligned_stats;

  const alignedEpList = mapEpisodeTop10Rows(
    episodeAligned.top10AlignedEpisodes
  );
  const misalignedEpList = mapEpisodeTop10Rows(
    episodeAligned.top10MisalignedEpisodes
  );
  const alignedShowList = mapShowTop10Rows(showAligned.top10AlignedShows);
  const misalignedShowList = mapShowTop10Rows(showAligned.top10MisalignedShows);

  return {
    reportingDate: overall?.reporting_date ?? '',
    totalImpressions: parseNum(overall?.total_impressions),
    showMatched: showMatch,
    showUnmatched: showUnmatch,
    episodeMatched: episodeMatch,
    episodeUnmatched: episodeUnmatch,
    episodeScored,
    episodeUnscored,
    top10ShowsByImpression: top10Shows,
    top10EpisodesByImpression: top10Eps,
    top5IabCategoriesByImpression: top5Iab ?? [],
    top5GenresByImpression: top5Genres ?? [],
    episodeAlignment: {
      impressionsAligned: episodeAligned.alignedImpressions,
      impressionsAlignedPercent: episodeAligned.alignedImpressionsPercent,
      episodesAligned: episodeAligned.alignedEpisodesCount,
      episodesAlignedPercent: episodeAligned.alignedEpisodesPercent,
      impressionsMisaligned: episodeAligned.misalignedImpressions,
      impressionsMisalignedPercent: episodeAligned.misalignedImpressionsPercent,
      episodesMisaligned: episodeAligned.misalignedEpisodesCount,
      episodesMisalignedPercent: episodeAligned.misalignedEpisodesPercent,
      top10MisalignedEpisodes:
        misalignedEpList.length > 0 ? misalignedEpList : [],
      top10AlignedEpisodes: alignedEpList.length > 0 ? alignedEpList : top10Eps,
    },
    showAlignment: {
      impressionsAligned: showAligned.alignedImpressions,
      impressionsAlignedPercent: showAligned.alignedImpressionsPercent,
      showsAligned: showAligned.alignedShowsCount,
      showsAlignedPercent: showAligned.alignedShowsPercent,
      impressionsMisaligned: showAligned.misalignedImpressions,
      impressionsMisalignedPercent: showAligned.misalignedImpressionsPercent,
      showsMisaligned: showAligned.misalignedShowsCount,
      showsMisalignedPercent: showAligned.misalignedShowsPercent,
      top10MisalignedShows:
        misalignedShowList.length > 0 ? misalignedShowList : [],
      top10AlignedShows:
        alignedShowList.length > 0 ? alignedShowList : top10Shows,
    },
  };
};

export const formatNumber = (n: number) => n?.toLocaleString();

/** Display API string for total impressions (e.g. in header when report object is unavailable). */
export const formatTotalImpressionsFromRaw = (
  raw: string | undefined
): string => {
  if (raw == null || raw === '') return '—';
  return formatNumber(parseNum(raw));
};

export const formatCampaignReportAsText = (
  report: CampaignReportData,
  campaignName: string
): string => {
  const fmt = (n: number) => n.toLocaleString();
  const lines: string[] = [
    `${campaignName} - Campaign Report`,
    '='.repeat(60),
    '',
    `Reporting date: ${report.reportingDate || '—'}`,
    `Total impressions: ${fmt(report.totalImpressions)}`,
    '',
    '--- Barometer Show Match ---',
    'Matched:',
    `  Barometer Matched Unique Shows: ${fmt(
      report.showMatched.uniqueShows
    )} (${report.showMatched.uniqueShowsPercent}%)`,
    `  Barometer Matched Unique Show Impressions: ${fmt(
      report.showMatched.impressions
    )} (${report.showMatched.impressionsPercent}%)`,
    'Unmatched:',
    `  Barometer Unmatched Unique Shows: ${fmt(
      report.showUnmatched.uniqueShows
    )} (${report.showUnmatched.uniqueShowsPercent}%)`,
    `  Barometer Unmatched Unique Show Impressions: ${fmt(
      report.showUnmatched.impressions
    )} (${report.showUnmatched.impressionsPercent}%)`,
    '',
    '--- Barometer Episode Match & Score ---',
    'Matched:',
    `  Barometer Matched Unique Episodes: ${fmt(
      report.episodeMatched.uniqueEpisodes
    )} (${report.episodeMatched.uniqueEpisodesPercent}%)`,
    `  Barometer Matched Unique Episode Impressions: ${fmt(
      report.episodeMatched.impressions
    )} (${report.episodeMatched.impressionsPercent}%)`,
    'Unmatched:',
    `  Barometer Unmatched Unique Episodes: ${fmt(
      report.episodeUnmatched.uniqueEpisodes
    )} (${report.episodeUnmatched.uniqueEpisodesPercent}%)`,
    `  Barometer Unmatched Unique Episode Impressions: ${fmt(
      report.episodeUnmatched.impressions
    )} (${report.episodeUnmatched.impressionsPercent}%)`,
    'Scored:',
    `  Barometer Scored Unique Episodes: ${fmt(
      report.episodeScored.uniqueEpisodes
    )} (${report.episodeScored.uniqueEpisodesPercent.toFixed(2)}%)`,
    `  Barometer Scored Unique Episode Impressions: ${fmt(
      report.episodeScored.impressions
    )} (${report.episodeScored.impressionsPercent.toFixed(2)}%)`,
    'Unscored:',
    `  Barometer Unscored Unique Episodes: ${fmt(
      report.episodeUnscored.uniqueEpisodes
    )} (${report.episodeUnscored.uniqueEpisodesPercent.toFixed(2)}%)`,
    `  Barometer Unscored Unique Episode Impressions: ${fmt(
      report.episodeUnscored.impressions
    )} (${report.episodeUnscored.impressionsPercent.toFixed(2)}%)`,
    '',
    '--- Top 10 Shows by Impression ---',
    ...report.top10ShowsByImpression.map(
      (item, i) => `  ${i + 1}. ${item.name} — ${fmt(item.impressions)}`
    ),
    '',
    '--- Top 10 Episodes by Impression ---',
    ...report.top10EpisodesByImpression.map(
      (item, i) => `  ${i + 1}. ${item.name} — ${fmt(item.impressions)}`
    ),
    '',
    '--- Top 5 Episode IAB Categories by Impression ---',
    ...(report.top5IabCategoriesByImpression.length > 0
      ? report.top5IabCategoriesByImpression.map(
          (item, i) => `  ${i + 1}. ${item}`
        )
      : ['  — —']),
    '',
    '--- Top 5 Genres by Impression ---',
    ...(report.top5GenresByImpression.length > 0
      ? report.top5GenresByImpression.map((item, i) => `  ${i + 1}. ${item}}`)
      : ['  — —']),
    '',
    '--- Episode Alignment Statistics ---',
    `Barometer Scored Unique Episodes Impressions Aligned: ${fmt(
      report.episodeAlignment.impressionsAligned
    )} (${report.episodeAlignment.impressionsAlignedPercent.toFixed(2)}%)`,
    `Barometer Scored Unique Episodes Aligned: ${fmt(
      report.episodeAlignment.episodesAligned
    )} (${report.episodeAlignment.episodesAlignedPercent.toFixed(2)}%)`,
    `Barometer Scored Unique Episodes Impressions Misaligned: ${fmt(
      report.episodeAlignment.impressionsMisaligned
    )} (${report.episodeAlignment.impressionsMisalignedPercent.toFixed(2)}%)`,
    `Barometer Scored Unique Episodes Misaligned: ${fmt(
      report.episodeAlignment.episodesMisaligned
    )} (${report.episodeAlignment.episodesMisalignedPercent.toFixed(2)}%)`,
    'Top 10 Misaligned Episodes by Impression:',
    ...(report.episodeAlignment.top10MisalignedEpisodes.length > 0
      ? report.episodeAlignment.top10MisalignedEpisodes.map((item, i) => {
          const reason =
            item.violationReason != null && item.violationReason !== ''
              ? ` — ${item.violationReason}`
              : '';
          const showPart =
            item.showTitle != null && item.showTitle.trim() !== ''
              ? `${item.showTitle.trim()} — `
              : '';
          return `  ${i + 1}. ${showPart}${item.name} — ${fmt(
            item.impressions
          )}${reason}`;
        })
      : ['  — —']),
    'Top 10 Aligned Episodes by Impression:',
    ...(report.episodeAlignment.top10AlignedEpisodes.length > 0
      ? report.episodeAlignment.top10AlignedEpisodes.map(
          (item, i) => `  ${i + 1}. ${item.name} — ${fmt(item.impressions)}`
        )
      : ['  — —']),
    '',
    '--- Show Alignment Statistics ---',
    `Barometer Matched Unique Shows Impressions Aligned: ${fmt(
      report.showAlignment.impressionsAligned
    )} (${report.showAlignment.impressionsAlignedPercent.toFixed(2)}%)`,
    `Barometer Matched Unique Shows Aligned: ${fmt(
      report.showAlignment.showsAligned
    )} (${report.showAlignment.showsAlignedPercent.toFixed(2)}%)`,
    `Barometer Matched Unique Shows Impressions Misaligned: ${fmt(
      report.showAlignment.impressionsMisaligned
    )} (${report.showAlignment.impressionsMisalignedPercent.toFixed(2)}%)`,
    `Barometer Matched Unique Shows Misaligned: ${fmt(
      report.showAlignment.showsMisaligned
    )} (${report.showAlignment.showsMisalignedPercent.toFixed(2)}%)`,
    'Top 10 Misaligned Shows by Impression:',
    ...(report.showAlignment.top10MisalignedShows.length > 0
      ? report.showAlignment.top10MisalignedShows.map((item, i) => {
          const reason =
            item.violationReason != null && item.violationReason !== ''
              ? ` — ${item.violationReason}`
              : '';
          return `  ${i + 1}. ${item.name} — ${fmt(item.impressions)}${reason}`;
        })
      : ['  — —']),
    'Top 10 Aligned Shows by Impression:',
    ...(report.showAlignment.top10AlignedShows.length > 0
      ? report.showAlignment.top10AlignedShows.map(
          (item, i) => `  ${i + 1}. ${item.name} — ${fmt(item.impressions)}`
        )
      : ['  —']),
  ];
  return lines.join('\n');
};

export const downloadReportAsTxt = (content: string, fileName: string) => {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${fileName.replace(/[^a-zA-Z0-9-_]/g, '_')}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};
